import { useEffect, useRef } from 'react'
import { Link, Outlet, useLocation } from 'react-router'
import Header from './Header'
import Footer from './Footer'
import { nietGevondenMeta, routeVoorPad } from '../routes'

/*
 * Focus bij routewissel.
 *
 * Bij een client-side navigatie vervangt React de inhoud maar verplaatst de browser
 * de focus niet: die blijft op de aangeklikte link staan, boven in de header. Een
 * toetsenbord- of schermlezergebruiker begint de nieuwe pagina dan halverwege de
 * navigatie en hoort niet dat er iets veranderd is. Daarom verplaatsen we de focus
 * expliciet naar <main>. Dat is ook wat een echte paginalading zou doen.
 *
 * De eerste render slaan we over: daar heeft de browser de focus al goed staan, en
 * focus stelen tijdens hydratatie zou de bezoeker uit een veld kunnen halen.
 */
function useFocusBijRoutewissel(hoofd: React.RefObject<HTMLElement | null>) {
  const location = useLocation()
  const eersteRender = useRef(true)

  useEffect(() => {
    if (eersteRender.current) {
      eersteRender.current = false
      return
    }

    if (location.hash) {
      const doel = document.querySelector(location.hash)
      if (doel instanceof HTMLElement) {
        doel.scrollIntoView()
        // Het anker is geen focusbaar element; -1 maakt het dat eenmalig.
        if (!doel.hasAttribute('tabindex')) doel.setAttribute('tabindex', '-1')
        doel.focus()
        return
      }
    }

    window.scrollTo(0, 0)
    hoofd.current?.focus()
  }, [location, hoofd])
}

/*
 * De scroll-reveal uit legacy/current/site.js. Identiek gedrag: elementen met
 * .reveal krijgen .is-in zodra ze in beeld komen, en meteen wanneer de bezoeker
 * bewegende animaties heeft uitgezet of de browser IntersectionObserver niet kent.
 *
 * Het beginpunt (opacity:0) staat in site.css achter `scripting: enabled`, zodat
 * een bezoeker zonder JavaScript geen onzichtbare pagina krijgt.
 */
function useReveal() {
  const location = useLocation()

  useEffect(() => {
    const elementen = document.querySelectorAll('.reveal:not(.is-in)')

    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      elementen.forEach((el) => el.classList.add('is-in'))
      return
    }

    const waarnemer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in')
            waarnemer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' },
    )

    elementen.forEach((el) => waarnemer.observe(el))
    return () => waarnemer.disconnect()
  }, [location])
}

export default function Layout() {
  const hoofd = useRef<HTMLElement>(null)
  const location = useLocation()
  useFocusBijRoutewissel(hoofd)
  useReveal()

  const route = routeVoorPad(location.pathname) ?? nietGevondenMeta

  return (
    <>
      {/*
        De skip-link is het eerste focusbare element van de pagina en wordt pas
        zichtbaar zodra hij focus krijgt (WCAG 2.2 AA, NFR-01). De bestaande site
        heeft er geen; dit is een toevoeging, geen afwijking van de vormgeving.
      */}
      <a href="#hoofdinhoud" className="skip-link">
        Direct naar de inhoud
      </a>

      <Header />

      <main id="hoofdinhoud" ref={hoofd} tabIndex={-1}>
        <Outlet />
      </main>

      <Footer
        variant={route.footer ?? 'volledig'}
        onderRechts={
          // Zoals op de bestaande site: de compacte footer verwijst naar de
          // privacyverklaring, behalve op de privacyverklaring zelf.
          route.pad === '/privacy' ? undefined : (
            <>
              <Link to="/privacy">Privacyverklaring</Link> · KvK: 42122048
            </>
          )
        }
      />
    </>
  )
}
